/**
 * Générateur — Connaître des grands nombres entiers
 * Attendu ID : 598 | Niveau : 6ème | Domaine : NC
 *
 * Labels :
 *   - Recomposer un entier à partir d'une décomposition positionnelle
 *   - Décomposer de grands nombres entiers
 *   - Écrire en chiffres des grands nombres donnés en lettres
 *   - Placer des grands nombres dans un tableau de numération
 *   - Problème contextuel avec de grands nombres
 *
 * Variantes estimées : >10 000 (moteur combinatoire de blocs sûrs)
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser, makeOrdonner,
} from '../../../utils';

// ─── Constantes ────────────────────────────────────────────────────────────

const ATTENDU_ID = 598;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Rangs étendus (jusqu'aux milliards) ───────────────────────────────────

const RANG_EXP = {
    'centaines de milliards': 11, 'dizaines de milliards': 10, 'milliards': 9,
    'centaines de millions': 8, 'dizaines de millions': 7, 'millions': 6,
    'centaines de milliers': 5, 'dizaines de milliers': 4, 'milliers': 3,
    'centaines': 2, 'dizaines': 1, 'unités': 0,
};

const RANG_VOISIN_HAUT = {
    'unités': 'dizaines', 'dizaines': 'centaines', 'centaines': 'milliers',
    'milliers': 'dizaines de milliers', 'dizaines de milliers': 'centaines de milliers',
    'centaines de milliers': 'millions', 'millions': 'dizaines de millions',
    'dizaines de millions': 'centaines de millions', 'centaines de millions': 'milliards',
    'milliards': 'dizaines de milliards',
};
const RANG_VOISIN_BAS = Object.fromEntries(Object.entries(RANG_VOISIN_HAUT).map(([k, v]) => [v, k]));

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Formate un entier avec espaces : 1234567 → "1 234 567" */
const formatInt = (n) => {
    let s = String(Math.abs(n));
    const groups = [];
    while (s.length > 3) { groups.push(s.slice(-3)); s = s.slice(0, -3); }
    groups.push(s);
    return groups.reverse().join(' ');
};

/** Recompose un entier depuis des termes [(coeff, rang), ...] */
const recompose = (termes) =>
    termes.reduce((sum, [c, r]) => sum + c * Math.pow(10, RANG_EXP[r]), 0);

/** Label de décomposition : "4 millions + 5 dizaines de milliers + 2 centaines"
 *  (singulier quand le coefficient vaut 1 : "1 centaine", "1 dizaine de milliers"…) */
const decompLabel = (termes) =>
    termes.map(([c, r]) => {
        if (c === 1) {
            const [tete, ...reste] = r.split(' ');
            return `1 ${[tete.replace(/s$/, ''), ...reste].join(' ')}`;
        }
        return `${c} ${r}`;
    }).join(' + ');

// ─── Pools ─────────────────────────────────────────────────────────────────

/**
 * Génère une décomposition aléatoire à 3 termes (tête / milieu / queue) avec
 * rangs strictement décroissants pour que la recomposition donne un nombre
 * mathématiquement cohérent et que les distracteurs de rangs voisins restent valides.
 *
 * Variétés possibles : 2 × 3 × 3 × 9³ ≈ 13 000 combinaisons.
 */
const RANGS_TETE_RECOMP = ['millions', 'milliards'];
const RANGS_MILIEU_RECOMP = ['centaines de milliers', 'dizaines de milliers', 'milliers'];
const RANGS_QUEUE_RECOMP = ['centaines', 'dizaines', 'unités'];

const genRecomposeTermes = () => [
    [randInt(1, 9), randomChoice(RANGS_TETE_RECOMP)],
    [randInt(1, 9), randomChoice(RANGS_MILIEU_RECOMP)],
    [randInt(1, 9), randomChoice(RANGS_QUEUE_RECOMP)],
];

/** Mapping position (0=unités) → rang pédagogique. */
const POSITION_RANG = [
    'unités', 'dizaines', 'centaines',
    'milliers', 'dizaines de milliers', 'centaines de milliers',
    'millions', 'dizaines de millions', 'centaines de millions',
    'milliards', 'dizaines de milliards', 'centaines de milliards',
];

/** Mapping rang → classe (unités / milliers / millions / milliards). */
const RANG_A_CLASSE = {
    'unités': 'unités', 'dizaines': 'unités', 'centaines': 'unités',
    'milliers': 'milliers', 'dizaines de milliers': 'milliers', 'centaines de milliers': 'milliers',
    'millions': 'millions', 'dizaines de millions': 'millions', 'centaines de millions': 'millions',
    'milliards': 'milliards', 'dizaines de milliards': 'milliards', 'centaines de milliards': 'milliards',
};

const CLASSES = ['unités', 'milliers', 'millions', 'milliards'];

/**
 * Génère un grand entier 7-10 chiffres (jamais commence par 0), avec ≥ 3 chiffres
 * non nuls. Retourne {formatted, chiffres[]} où chiffres = [{chiffre, rang, classe, position}].
 */
const genGrandEntier = () => {
    while (true) {
        const longueur = randInt(7, 10);
        const digits = [String(randInt(1, 9))];
        for (let i = 1; i < longueur; i++) digits.push(String(randInt(0, 9)));
        const s = digits.join('');
        const chiffres = [];
        for (let pos = 0; pos < longueur; pos++) {
            const chiffre = parseInt(s[longueur - 1 - pos]);
            if (chiffre === 0) continue;
            const rang = POSITION_RANG[pos];
            chiffres.push({ chiffre, rang, classe: RANG_A_CLASSE[rang], position: pos });
        }
        if (chiffres.length < 3) continue; // garantir assez de chiffres non nuls pour les exos
        return { formatted: formatInt(parseInt(s)), chiffres };
    }
};

/** Ne garde que les chiffres présents dans UNE SEULE classe (question sans ambiguïté). */
const unambigousChiffres = (chiffres) => {
    const chiffreToClasses = {};
    chiffres.forEach(c => {
        if (!chiffreToClasses[c.chiffre]) chiffreToClasses[c.chiffre] = new Set();
        chiffreToClasses[c.chiffre].add(c.classe);
    });
    return chiffres.filter(c => chiffreToClasses[c.chiffre].size === 1);
};

/** Donnée pour genTableauQcm : [numS, chiffre, correct, d1, d2, d3]. */
const genClasseQcmData = () => {
    let formatted, candidats;
    for (let i = 0; i < 30; i++) {
        const res = genGrandEntier();
        formatted = res.formatted;
        candidats = unambigousChiffres(res.chiffres);
        if (candidats.length >= 1) break;
    }
    const chosen = randomChoice(candidats);
    const correct = `La classe des ${chosen.classe}`;
    const distracteurs = CLASSES.filter(c => c !== chosen.classe).map(c => `La classe des ${c}`);
    return [formatted, String(chosen.chiffre), correct, ...distracteurs];
};

/** Donnée pour genTableauSaisie : [numS, rang1, ch1, rang2, ch2]. */
const genSaisieRangData = () => {
    const { formatted, chiffres } = genGrandEntier();
    // 2 chiffres distincts (rangs différents, garanti par la structure du nombre)
    const [c1, c2] = shuffle([...chiffres]).slice(0, 2);
    return [formatted, c1.rang, String(c1.chiffre), c2.rang, String(c2.chiffre)];
};

/** Donnée pour genTableauVf : [numS, affirmations[[texte, 'Vrai'|'Faux']]].
 *  Pour chaque chiffre du nombre, on récupère l'ensemble des classes où il apparaît.
 *  - Affirmation VRAIE = "chiffre X dans classe Y" où Y ∈ classes(X)
 *  - Affirmation FAUSSE = "chiffre X dans classe Z" où Z ∉ classes(X) (jamais d'ambiguïté) */
const genTableauVfData = () => {
    let formatted, chiffreGroups;
    for (let i = 0; i < 50; i++) {
        const res = genGrandEntier();
        formatted = res.formatted;
        // Groupe par valeur de chiffre → ensemble des classes où il apparaît
        const byChiffre = {};
        res.chiffres.forEach(c => {
            if (!byChiffre[c.chiffre]) byChiffre[c.chiffre] = { chiffre: c.chiffre, classes: new Set() };
            byChiffre[c.chiffre].classes.add(c.classe);
        });
        // Ne garder que les chiffres avec au moins 1 classe où ils n'apparaissent PAS (pour pouvoir construire un FAUX)
        chiffreGroups = Object.values(byChiffre).filter(g => g.classes.size < CLASSES.length);
        if (chiffreGroups.length >= 2) break;
    }
    const picked = shuffle([...chiffreGroups]).slice(0, 2);
    const affirmations = [];
    // 2 vraies : "chiffre X dans une des classes où il apparaît"
    picked.forEach(g => {
        const classe = randomChoice([...g.classes]);
        affirmations.push([`Le chiffre ${g.chiffre} est dans la classe des ${classe}`, 'Vrai']);
    });
    // 2 fausses : "chiffre X dans une classe où il n'apparaît PAS"
    picked.forEach(g => {
        const wrongClasses = CLASSES.filter(cl => !g.classes.has(cl));
        const wrongClasse = randomChoice(wrongClasses);
        affirmations.push([`Le chiffre ${g.chiffre} est dans la classe des ${wrongClasse}`, 'Faux']);
    });
    return [formatted, shuffle(affirmations)];
};

/** Donnée pour genCtxOrdonner : tableau de 4 nombres triés croissants. */
const genOrdonnerData = () => {
    const nums = new Set();
    while (nums.size < 4) nums.add(parseInt(genGrandEntier().formatted.replace(/ /g, '')));
    return [...nums].sort((a, b) => a - b).map(formatInt);
};

/** Donnée pour genCtxClasser : 2 catégories avec 3 nombres chacune. */
const CLASSER_CATS = [
    { name: "Moins d'un million", gen: () => randInt(1000, 999999) },
    { name: "Entre un million et un milliard", gen: () => randInt(1000000, 999999999) },
    { name: "Plus d'un milliard", gen: () => randInt(1000000000, 99999999999) },
];

const genClasserData = () => {
    const cats = shuffle([...CLASSER_CATS]).slice(0, 2);
    return cats.map(cat => {
        const items = new Set();
        while (items.size < 3) items.add(formatInt(cat.gen()));
        return { name: cat.name, items: [...items] };
    });
};

// ─── Moteur « nombres en lettres » par blocs sûrs (enrichissement 2026-07-04) ──
// Principe : on ne stocke JAMAIS de nombres complets en lettres. On tire un
// NOMBRE en chiffres construit par classes (milliards / millions / mille / unités),
// puis les mots en sont dérivés par assemblage de blocs < 1000 issus d'une liste
// blanche (orthographe traditionnelle, espaces entre blocs — convention du pool
// historique). Les formes à accord ambigu sont EXCLUES du tirage :
//   - « et un » / « et onze » (21, 31, 41, 51, 61, 71)
//   - « quatre-vingts » nu (80, 180, 280…)
//   - centaines rondes ≥ 200 (accord « cents »)
// Auto-vérification : chaque écriture produite est RE-CONVERTIE en nombre par la
// table inverse ; toute divergence lève une erreur (S-CRASH au harnais, seed rejouable).

const MOTS_UNITES_598 = {
    1: 'un', 2: 'deux', 3: 'trois', 4: 'quatre', 5: 'cinq', 6: 'six', 7: 'sept',
    8: 'huit', 9: 'neuf', 10: 'dix', 11: 'onze', 12: 'douze', 13: 'treize',
    14: 'quatorze', 15: 'quinze', 16: 'seize', 17: 'dix-sept', 18: 'dix-huit', 19: 'dix-neuf',
};
const MOTS_DIZAINES_598 = { 20: 'vingt', 30: 'trente', 40: 'quarante', 50: 'cinquante', 60: 'soixante' };

/** Écrit 1-99 (formes sûres uniquement — jamais de « et un », jamais 80 nu). */
const sous100EnLettres598 = (n) => {
    if (MOTS_UNITES_598[n]) return MOTS_UNITES_598[n];
    if (MOTS_DIZAINES_598[n]) return MOTS_DIZAINES_598[n];
    if (n === 70) return 'soixante-dix';
    if (n === 90) return 'quatre-vingt-dix';
    const d = Math.floor(n / 10) * 10;
    const u = n - d;
    if (MOTS_DIZAINES_598[d] && u >= 2) return `${MOTS_DIZAINES_598[d]}-${MOTS_UNITES_598[u]}`;
    if (d === 70 && u >= 2) return `soixante-${MOTS_UNITES_598[10 + u]}`;
    if (d === 80 && u >= 1) return `quatre-vingt-${MOTS_UNITES_598[u]}`;
    if (d === 90 && u >= 1) return `quatre-vingt-${MOTS_UNITES_598[10 + u]}`;
    throw new Error(`gen_598 : ${n} hors liste blanche sous-100`);
};

/** Écrit un bloc 1-999 (centaines rondes ≥ 200 interdites : accord « cents »). */
const blocEnLettres598 = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    if (h === 0) return sous100EnLettres598(n);
    if (r === 0) {
        if (h === 1) return 'cent';
        throw new Error(`gen_598 : centaine ronde ${n} exclue (accord « cents »)`);
    }
    return `${h === 1 ? 'cent' : `${MOTS_UNITES_598[h]} cent`} ${sous100EnLettres598(r)}`;
};

/** Liste blanche des valeurs de bloc autorisées (aucune forme à piège d'accord). */
const BLOCS_SURS_598 = [];
for (let b = 1; b <= 999; b++) {
    const r = b % 100;
    if ([21, 31, 41, 51, 61, 71].includes(r)) continue; // « et un » / « et onze »
    if (r === 80) continue;                              // « quatre-vingts » (accord)
    if (b >= 200 && r === 0) continue;                   // centaines rondes (accord « cents »)
    BLOCS_SURS_598.push(b);
}
const BLOCS_SIMPLES_598 = BLOCS_SURS_598.filter(b => b <= 99);

const tireBloc598 = () => randomChoice(Math.random() < 0.5 ? BLOCS_SIMPLES_598 : BLOCS_SURS_598);

/** nombre → mots (assemblage par gabarit de classes, jamais deux blocs nus côte à côte). */
const nombreEnLettres598 = (n) => {
    const g = Math.floor(n / 1e9);
    const m = Math.floor(n / 1e6) % 1000;
    const k = Math.floor(n / 1000) % 1000;
    const u = n % 1000;
    const parts = [];
    if (g) parts.push(`${blocEnLettres598(g)} milliard${g > 1 ? 's' : ''}`);
    if (m) parts.push(`${blocEnLettres598(m)} million${m > 1 ? 's' : ''}`);
    if (k) parts.push(k === 1 ? 'mille' : `${blocEnLettres598(k)} mille`);
    if (u) parts.push(blocEnLettres598(u));
    if (!parts.length) throw new Error('gen_598 : zéro impossible à écrire en lettres');
    return parts.join(' ');
};

/** Table inverse mot → valeur (construite depuis les tables directes). */
const VALEUR_DES_MOTS_598 = (() => {
    const map = {};
    BLOCS_SIMPLES_598.forEach(b => { map[sous100EnLettres598(b)] = b; });
    return map;
})();

/** mots → nombre — sert UNIQUEMENT à l'auto-vérification aller-retour. */
const lettresVersNombre598 = (mots) => {
    let total = 0;
    let bloc = 0;
    mots.split(' ').forEach(tok => {
        if (tok === 'milliard' || tok === 'milliards') { total += bloc * 1e9; bloc = 0; }
        else if (tok === 'million' || tok === 'millions') { total += bloc * 1e6; bloc = 0; }
        else if (tok === 'mille') { total += (bloc || 1) * 1000; bloc = 0; }
        else if (tok === 'cent') { bloc = (bloc || 1) * 100; }
        else {
            const v = VALEUR_DES_MOTS_598[tok];
            if (v === undefined) throw new Error(`gen_598 : mot inconnu « ${tok} »`);
            bloc += v;
        }
    });
    return total + bloc;
};

/** nombre → mots AUTO-VÉRIFIÉS (l'aller-retour doit redonner n, sinon erreur). */
const motsSurs598 = (n) => {
    const mots = nombreEnLettres598(n);
    const retour = lettresVersNombre598(mots);
    if (retour !== n) throw new Error(`gen_598 : auto-vérification échouée — ${n} → « ${mots} » → ${retour}`);
    return mots;
};

// ─── Tirage de grands nombres par gabarit de classes ───────────────────────

const SCHEMAS_598 = ['mille', 'millions', 'millions_mille', 'millions_unites', 'milliards', 'milliards_millions'];

const construitNombre598 = (schema, petitsBlocsTete = false) => {
    const tete = () => (petitsBlocsTete ? randomChoice(BLOCS_SIMPLES_598) : tireBloc598());
    switch (schema) {
        case 'mille':              return tete() * 1000;
        case 'millions':           return tete() * 1e6;
        case 'millions_mille':     return tete() * 1e6 + tireBloc598() * 1000;
        case 'millions_unites':    return tete() * 1e6 + tireBloc598();
        case 'milliards':          return randomChoice(BLOCS_SIMPLES_598) * 1e9;
        case 'milliards_millions': return randomChoice(BLOCS_SIMPLES_598) * 1e9 + tireBloc598() * 1e6;
        default:                   return tete() * 1000;
    }
};

const classesDe598 = (n) => ({
    g: Math.floor(n / 1e9),
    m: Math.floor(n / 1e6) % 1000,
    k: Math.floor(n / 1000) % 1000,
    u: n % 1000,
});

/** Distracteurs en CHIFFRES par misconception — ≠ correct par construction. */
const distracteursChiffres598 = (n) => {
    const { g, m, k, u } = classesDe598(n);
    const candidats = [];
    if (n < 1e9) candidats.push(n * 1000);                        // classe lue trop haut (mille → million…)
    if (n % 1000 === 0 && n >= 1e6) candidats.push(n / 1000);     // classe lue trop bas
    if (m && k && !u) candidats.push(m * 1e6 + k);                // bloc des mille rétrogradé en unités
    if (m && k && !u && m !== k) candidats.push(k * 1e6 + m * 1000); // blocs échangés
    if (g && m && !k) candidats.push(m * 1e9 + g * 1e6);          // blocs échangés (milliards)
    if (m && u && !k) candidats.push(m * 1e6 + u * 1000);         // unités lues « mille »
    if (m && u && !k && m !== u) candidats.push(u * 1e6 + m);     // blocs échangés (millions/unités)
    candidats.push(n * 10);                                        // un zéro de trop
    if (n % 10 === 0) candidats.push(n / 10);                      // un zéro de moins
    const uniques = [...new Set(candidats)].filter(c => c !== n && c > 0);
    const picked = shuffle(uniques).slice(0, 3);
    while (picked.length < 3) picked.push(n * Math.pow(100, picked.length + 1));
    return picked.map(formatInt);
};

/** Nombres voisins dont l'écriture en lettres reste sûre (pièges « se lit »). */
const voisinsEnLettres598 = (n) => {
    const { g, m, k } = classesDe598(n);
    const candidats = [];
    if (n < 1e9) candidats.push(n * 1000);
    if (n % 1000 === 0 && n >= 1e6) candidats.push(n / 1000);
    if (m && k && m !== k) candidats.push(k * 1e6 + m * 1000);
    if (m && k) candidats.push(m * 1e6 + k);
    if (g && m && g !== m) candidats.push(m * 1e9 + g * 1e6);
    const result = [];
    [...new Set(candidats)].filter(c => c !== n && c >= 1000 && c < 1e12).forEach(c => {
        try { result.push({ n: c, mots: motsSurs598(c) }); } catch (e) { /* forme non sûre : écartée */ }
    });
    return shuffle(result);
};

// ─── Contextes pour les problèmes (grandeurs plausibles par gabarit) ────────

const CONTEXTES_598 = [
    { avant: 'Ce pays compte', unite: 'habitants', schemas: ['millions', 'millions_mille'] },
    { avant: 'Cette ville compte', unite: 'habitants', schemas: ['mille'] },
    { avant: 'Ce réseau social compte', unite: 'utilisateurs', schemas: ['millions', 'milliards_millions'] },
    { avant: 'Ce stade peut accueillir', unite: 'spectateurs', schemas: ['mille'], petitsBlocs: true },
    { avant: 'Cette sonde spatiale a parcouru', unite: 'kilomètres', schemas: ['millions', 'milliards'] },
    { avant: 'Cette vidéo a été vue par', unite: 'personnes', schemas: ['millions', 'millions_mille'] },
    { avant: 'Cette chaîne a', unite: 'abonnés', schemas: ['millions', 'millions_mille'] },
    { avant: 'Le budget de la région est de', unite: 'euros', schemas: ['millions', 'milliards_millions'] },
    { avant: 'Cette galaxie contient environ', unite: 'étoiles', schemas: ['milliards', 'milliards_millions'] },
    { avant: 'Cette usine produit chaque année', unite: 'pièces', schemas: ['millions', 'mille'] },
    { avant: 'Cet appartement vaut', unite: 'euros', schemas: ['mille'] },
    { avant: 'Cette application totalise', unite: 'téléchargements', schemas: ['millions', 'milliards_millions'] },
    { avant: 'Ce marathon attire', unite: 'spectateurs', schemas: ['mille'], petitsBlocs: true },
    { avant: 'Cette bibliothèque possède', unite: 'livres', schemas: ['mille', 'millions'] },
];

const VOYELLE_598 = /^[aeiouéèêh]/;

/** Assemble la phrase de contexte ; « de/d' » requis après millions/milliards. */
const phraseContexte598 = (ctx, mots, avecEmphase) => {
    const enLettres = avecEmphase ? `<em>${mots}</em>` : mots;
    const finitParClasse = /(million|millions|milliard|milliards)$/.test(mots);
    const liaison = finitParClasse
        ? (VOYELLE_598.test(ctx.unite) ? `d'${ctx.unite}` : `de ${ctx.unite}`)
        : ctx.unite;
    return `${ctx.avant} ${enLettres} ${liaison}.`;
};

const genContexteMots598 = () => {
    const ctx = randomChoice(CONTEXTES_598);
    const schema = randomChoice(ctx.schemas);
    const n = construitNombre598(schema, ctx.petitsBlocs);
    const mots = motsSurs598(n);
    return { ctx, schema, n, mots };
};

// ─── Helpers distracteurs recomposition ────────────────────────────────────

const recomposeDistractors = (termes) => {
    const correct = recompose(termes);
    const distractors = [];
    // D1 : décaler le 2ème terme d'un rang vers le haut
    if (termes.length >= 2) {
        const [c, r] = termes[1];
        const rUp = RANG_VOISIN_HAUT[r] || r;
        const d1 = recompose([termes[0], [c, rUp], ...termes.slice(2)]);
        if (d1 !== correct) distractors.push(d1);
    }
    // D2 : décaler le 2ème terme d'un rang vers le bas
    if (termes.length >= 2) {
        const [c, r] = termes[1];
        const rDown = RANG_VOISIN_BAS[r] || r;
        const d2 = recompose([termes[0], [c, rDown], ...termes.slice(2)]);
        if (d2 !== correct && !distractors.includes(d2)) distractors.push(d2);
    }
    // D3 : décaler le dernier terme d'un rang vers le haut
    if (termes.length >= 3) {
        const [c, r] = termes[termes.length - 1];
        const rUp = RANG_VOISIN_HAUT[r] || r;
        const d3 = recompose([...termes.slice(0, -1), [c, rUp]]);
        if (d3 !== correct && !distractors.includes(d3)) distractors.push(d3);
    }
    // Fallback
    while (distractors.length < 3) {
        const fb = correct * 10 / (distractors.length + 1);
        distractors.push(Math.round(fb) !== correct ? Math.round(fb) : correct + 1000 * (distractors.length + 1));
    }
    return distractors.slice(0, 3).map(formatInt);
};

// ─── EXERCICE 1 : Recomposer ──────────────────────────────────────────────

const L1 = 'Recomposer un entier à partir d\'une décomposition positionnelle';

const genRecomposeQcm = () => {
    const termes = genRecomposeTermes();
    const correctS = formatInt(recompose(termes));
    const desc = decompLabel(termes);
    const distractors = recomposeDistractors(termes);
    let choices = [...new Set([correctS, ...distractors])].slice(0, 4);
    while (choices.length < 4) choices.push(formatInt(recompose(termes) + randInt(1, 9) * 1000));
    const texte = `<p>Quel nombre correspond à : <strong>${desc}</strong> ?</p>`;
    return makeQcm(choices, correctS, texte, opts(L1));
};

const genRecomposeTexte = () => {
    const termes = genRecomposeTermes();
    const correctS = formatInt(recompose(termes));
    const desc = decompLabel(termes);
    const texte = `<p>Écrire en chiffres : <strong>${desc}</strong></p>`;
    return makeSaisie(correctS, texte, opts(L1));
};

const genRecomposeDrag = () => {
    const termes = genRecomposeTermes();
    const correctS = formatInt(recompose(termes));
    const desc = decompLabel(termes);
    const phrase = `${desc} s'écrit {{${correctS}}}.`;
    const distractors = recomposeDistractors(termes);
    const texte = '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L1), distracteurs: distractors });
};

const genRecomposeSaisie = () => {
    const termes = genRecomposeTermes();
    const correctS = formatInt(recompose(termes));
    const desc = decompLabel(termes);
    const phrase = `${desc} s'écrit {{${correctS}}}.`;
    const texte = '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L1), mode: 'saisie' });
};

// ─── EXERCICE 2 : Décomposer ──────────────────────────────────────────────

const L2 = 'Décomposer de grands nombres entiers';

/**
 * Termes générés pour la décomposition : coefficients tous DISTINCTS (sinon
 * l'échange des rangs de deux termes de même coefficient donnerait un
 * distracteur de même somme = deuxième bonne réponse), et rangs choisis pour
 * que les décalages voisins ne dupliquent pas un rang déjà présent.
 */
const genDecomposeTermes = () => {
    for (let i = 0; i < 50; i++) {
        const termes = genRecomposeTermes();
        if (new Set(termes.map(t => t[0])).size < termes.length) continue;
        const r1 = termes[1][1];
        if (RANG_VOISIN_HAUT[r1] === termes[0][1] || RANG_VOISIN_BAS[r1] === termes[2][1]) continue;
        return termes;
    }
    // Repli sûr (rangs sans voisinage conflictuel, coefficients distincts)
    const [a, b, c] = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 3);
    return [[a, 'millions'], [b, 'dizaines de milliers'], [c, 'dizaines']];
};

const genDecomposeQcm = () => {
    const termes = genDecomposeTermes();
    const numS = formatInt(recompose(termes));
    const correct = decompLabel(termes);
    // D1 : décaler le 2ème rang vers le haut
    const [c1, r1] = termes[1] || termes[0];
    const d1 = decompLabel([termes[0], [c1, RANG_VOISIN_HAUT[r1] || r1], ...termes.slice(2)]);
    // D2 : décaler vers le bas
    const d2 = decompLabel([termes[0], [c1, RANG_VOISIN_BAS[r1] || r1], ...termes.slice(2)]);
    // D3 : inverser les rangs du 2e et 3e terme (en préservant les termes suivants si ≥ 4)
    let d3;
    if (termes.length >= 3) {
        d3 = decompLabel([termes[0], [termes[1][0], termes[2][1]], [termes[2][0], termes[1][1]], ...termes.slice(3)]);
    } else {
        d3 = decompLabel([...termes.slice(0, -1), [termes[termes.length - 1][0], RANG_VOISIN_HAUT[termes[termes.length - 1][1]] || termes[termes.length - 1][1]]]);
    }
    let choices = [...new Set([correct, d1, d2, d3])].slice(0, 4);
    const texte = `<p>Quelle décomposition est correcte pour <strong>${numS}</strong> ?</p>`;
    return makeQcm(choices, correct, texte, opts(L2));
};

/**
 * Génère algorithmiquement une décomposition d'un grand nombre.
 * Aléatoirement par classe ("40 millions + 142 milliers") ou par rang ("4 dizaines de millions + 1 centaine de milliers + ...").
 * Retourne { phrase, distracteurs, formatted } pour les exercices drag et saisie.
 */
const genDecompAlgo = () => {
    const n = randInt(1000000, 99999999); // 7-8 chiffres
    const s = String(n);
    const formatted = formatInt(n);
    const byClasse = Math.random() > 0.5;

    if (byClasse) {
        // Découper par classes de 3 chiffres depuis la droite
        const NOMS_CLASSES = ['unités', 'milliers', 'millions', 'milliards'];
        const groups = [];
        let rest = s;
        while (rest.length > 3) { groups.unshift(rest.slice(-3)); rest = rest.slice(0, -3); }
        if (rest) groups.unshift(rest);

        const termes = groups.map((group, i) => ({
            val: parseInt(group),
            nom: NOMS_CLASSES[groups.length - 1 - i],
        })).filter(t => t.val > 0);

        const phrase = `${formatted} = ${termes.map(t => `{{${t.val}}} ${t.nom}`).join(' + ')}`;
        // Distracteurs : 0 + sous-parties des valeurs correctes
        const correctVals = new Set(termes.map(t => String(t.val)));
        const dists = ['0'];
        termes.forEach(t => {
            const vs = String(t.val);
            if (vs.length >= 2) dists.push(vs.slice(0, -1)); // "142" → "14"
            if (vs.length >= 2) dists.push(vs.slice(1));      // "142" → "42"
        });
        const distracteurs = [...new Set(dists)].filter(d => !correctVals.has(d)).slice(0, 4);
        return { phrase, distracteurs, formatted };
    }

    // Par rang : chaque chiffre non-nul avec son rang
    const RANGS_ORDERED = [
        'dizaines de millions', 'millions', 'centaines de milliers',
        'dizaines de milliers', 'milliers', 'centaines', 'dizaines', 'unités',
    ];
    // Padder à 8 chiffres pour matcher les rangs
    const padded = s.padStart(8, '0');
    const termes = RANGS_ORDERED.map((rang, i) => ({
        val: parseInt(padded[i]),
        nom: rang,
    })).filter(t => t.val > 0);

    const phrase = `${formatted} = ${termes.map(t => `{{${t.val}}} ${t.nom}`).join(' + ')}`;
    // Distracteurs : 0 + autres chiffres du nombre non utilisés comme réponses
    const correctVals = new Set(termes.map(t => String(t.val)));
    const digitsInNumber = [...new Set(s.split(''))].filter(d => !correctVals.has(d));
    const distracteurs = ['0', ...digitsInNumber].filter((v, i, a) => a.indexOf(v) === i).slice(0, 4);
    return { phrase, distracteurs, formatted };
};

const genDecomposeDrag = () => {
    const { phrase, distracteurs } = genDecompAlgo();
    const texte = '<p>Complète la décomposition :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L2), distracteurs });
};

const genDecomposeSaisie = () => {
    const { phrase } = genDecompAlgo();
    const texte = '<p>Complète la décomposition :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L2), mode: 'saisie' });
};

// ─── EXERCICE 3 : Mots → chiffres ─────────────────────────────────────────

const L3 = 'Écrire en chiffres des grands nombres donnés en lettres';

const genMotsQcm = () => {
    const n = construitNombre598(randomChoice(SCHEMAS_598));
    const mots = motsSurs598(n);
    const correct = formatInt(n);
    const choices = [correct, ...distracteursChiffres598(n)];
    const texte = `<p>Quelle est l'écriture en chiffres de <em>« ${mots} »</em> ?</p>`;
    return makeQcm(choices, correct, texte, opts(L3));
};

const genMotsTexte = () => {
    const { ctx, n, mots } = genContexteMots598();
    const texte = `<p>${phraseContexte598(ctx, mots, true)}</p><p>Écrire ce nombre en chiffres.</p>`;
    return makeSaisie(formatInt(n), texte, opts(L3));
};

const genMotsDrag = () => {
    const n = construitNombre598(randomChoice(SCHEMAS_598));
    const mots = motsSurs598(n);
    const correct = formatInt(n);
    const phrase = `${mots.charAt(0).toUpperCase() + mots.slice(1)} s'écrit {{${correct}}}.`;
    const texte = '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L3), distracteurs: distracteursChiffres598(n) });
};

const genMotsRelier = () => {
    const paires = [];
    if (Math.random() < 0.5) {
        // Quatuor « voisins piégeux » : les MÊMES blocs à des rangs différents —
        // impossible à relier sans lire réellement les classes.
        const X = tireBloc598();
        let Y = tireBloc598();
        while (Y === X) Y = tireBloc598();
        const nombres = shuffle([
            X * 1e6 + Y * 1000, // « X millions Y mille »
            X * 1e6 + Y,        // « X millions Y »
            Y * 1e6 + X * 1000, // « Y millions X mille »
            X * 1000 + Y,       // « X mille Y »
        ]);
        nombres.forEach(n => paires.push({ source: motsSurs598(n), target: formatInt(n) }));
    } else {
        const nombres = new Set();
        while (nombres.size < 4) nombres.add(construitNombre598(randomChoice(SCHEMAS_598)));
        [...nombres].forEach(n => paires.push({ source: motsSurs598(n), target: formatInt(n) }));
    }
    const texte = '<p>Relier chaque expression en lettres à son écriture en chiffres.</p>';
    return makeRelier(paires, texte, opts(L3));
};

// ─── EXERCICE 4 : Tableau de numération ───────────────────────────────────

const L4 = 'Placer des grands nombres dans un tableau de numération';

const genTableauQcm = () => {
    const [numS, chiffre, correct, d1, d2, d3] = genClasseQcmData();
    const choices = [correct, d1, d2, d3];
    const texte = `<p>Dans le nombre <strong>${numS}</strong>, dans quelle <strong>classe</strong> se trouve le chiffre <strong>${chiffre}</strong> ?</p>`;
    return makeQcm(choices, correct, texte, opts(L4));
};

const genTableauSaisie = () => {
    const [numS, rang1, ch1, rang2, ch2] = genSaisieRangData();
    const phrase = `Dans ${numS} : le chiffre des ${rang1} est {{${ch1}}} et le chiffre des ${rang2} est {{${ch2}}}.`;
    const texte = '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L4), mode: 'saisie' });
};

const genTableauVf = () => {
    const [numS, affirmations] = genTableauVfData();
    const seen = new Set();
    const lignes = [];
    const bonnesReponses = [];
    affirmations.forEach(([text, ans]) => {
        if (!seen.has(text)) {
            seen.add(text);
            lignes.push(text);
            bonnesReponses.push(ans === 'Vrai' ? 0 : 1);
        }
    });
    const texte = `<p>Vrai ou faux ? (Nombre : <strong>${numS}</strong>)</p>`;
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L4));
};

// ─── EXERCICE 5 : Problème contextuel ─────────────────────────────────────

const L5 = 'Problème contextuel avec de grands nombres';

const genCtxQcm = () => {
    const { ctx, schema, n, mots } = genContexteMots598();
    const voisins = voisinsEnLettres598(n).slice(0, 3);
    while (voisins.length < 3) {
        const alt = construitNombre598(schema, ctx.petitsBlocs);
        if (alt !== n && !voisins.some(v => v.n === alt)) voisins.push({ n: alt, mots: motsSurs598(alt) });
    }
    const correct = phraseContexte598(ctx, mots, false);
    const choices = [correct, ...voisins.map(v => phraseContexte598(ctx, v.mots, false))];
    const texte = `<p>Le nombre <strong>${formatInt(n)}</strong> se lit :</p>`;
    return makeQcm(choices, correct, texte, opts(L5));
};

const genCtxTexte = () => {
    const { ctx, n, mots } = genContexteMots598();
    const texte = `<p>Écris en chiffres le nombre en italique.</p><p>${phraseContexte598(ctx, mots, true)}</p>`;
    return makeSaisie(formatInt(n), texte, opts(L5));
};

const genCtxOrdonner = () => {
    const ordered = genOrdonnerData();
    const texte = '<p>Ranger ces nombres <strong>du plus petit au plus grand</strong>.</p>';
    return makeOrdonner(ordered, texte, opts(L5));
};

const genCtxClasser = () => {
    const categories = genClasserData();
    const texte = '<p>Classe chaque nombre dans la bonne catégorie.</p>';
    return makeClasser(categories, texte, opts(L5));
};

// ─── Registre et catégories ───────────────────────────────────────────────

const GENERATORS = {
    recompose_qcm_unique:        genRecomposeQcm,
    recompose_texte:             genRecomposeTexte,
    recompose_texte_trou_drag:   genRecomposeDrag,
    recompose_texte_trou_saisie: genRecomposeSaisie,
    decompose_qcm_unique:        genDecomposeQcm,
    decompose_texte_trou_drag:   genDecomposeDrag,
    decompose_texte_trou_saisie: genDecomposeSaisie,
    mots_qcm_unique:             genMotsQcm,
    mots_texte:                  genMotsTexte,
    mots_texte_trou_drag:        genMotsDrag,
    mots_relier:                 genMotsRelier,
    tableau_qcm_unique:          genTableauQcm,
    tableau_texte_trou_saisie:   genTableauSaisie,
    tableau_vf:                  genTableauVf,
    ctx_qcm_unique:              genCtxQcm,
    ctx_texte:                   genCtxTexte,
    ctx_ordonner:                genCtxOrdonner,
    ctx_classer:                 genCtxClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'recompose', label: L1, formatsList: buildFormats('recompose') },
    { id: 'decompose', label: L2, formatsList: buildFormats('decompose') },
    { id: 'mots', label: L3, formatsList: buildFormats('mots') },
    { id: 'tableau', label: L4, formatsList: buildFormats('tableau') },
    { id: 'ctx', label: L5, formatsList: buildFormats('ctx') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
